import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query'
import {
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AgendaGrid,
  type AgendaMutationHandlers,
  type ResizeJobPayload,
  type ScheduleJobPayload,
  type UnassignJobOptions,
} from '@/components/jobs/AgendaGrid'
import { JobFormDialog } from '@/components/jobs/JobFormDialog'
import { JobDetailDrawer } from '@/components/jobs/JobDetailDrawer'
import { RescheduleJobDrawerContent } from '@/components/jobs/JobActionDialogs'
import { UnassignedTray } from '@/components/jobs/UnassignedTray'
import { PaymentCreateDrawerContent } from '@/components/payments/PaymentCreateDrawerContent'
import { PaymentDetailDrawerContent } from '@/components/payments/PaymentDetailDrawerContent'
import { PaymentDrawerShell } from '@/components/payments/PaymentDrawerShell'
import { rangeFor } from '@/components/jobs/agenda/agenda-date-range'
import { applyAgendaJobSchedulePatch } from '@/components/jobs/agenda/agenda-cache'
import { technicianFirstName } from '@/components/jobs/agenda/grid-helpers'
import { JobDetailPreloadRoot } from '@/components/jobs/agenda/JobDetailPreloadRoot'
import { useAgendaJobPreload } from '@/components/jobs/agenda/useAgendaJobPreload'
import JobReportWizardPage from '@/pages/jobs/JobReportWizardPage'
import { useAgendaJobChanges, useAgendaTechnicianPages } from '@/hooks/useJobs'
import { useTechniciansList } from '@/hooks/useTechnicians'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { useServiceCategoryOptions } from '@/hooks/useServiceCategoryOptions'
import { jobsApi } from '@/services/jobs'
import { cn } from '@/lib/utils'
import {
  JOB_STATUS,
  type AgendaQuery,
  type AgendaSchedule,
  type ApiList,
  type ApiResponse,
  type Job,
  type JobStatus,
  type Technician,
} from '@/types'
import { Avatar, initialsFromName } from '@/components/ui/avatar'
import {
  FORM_CONTROL_OPEN_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'

type View = 'day' | 'week' | 'month'

const JOB_STATUS_VALUES: readonly JobStatus[] = Object.values(JOB_STATUS)
type StatusFilter = JobStatus | 'all'

function isJobStatus(value: string | null): value is JobStatus {
  return JOB_STATUS_VALUES.includes(value as JobStatus)
}

const statusOptions: readonly StatusFilter[] = [
  'all',
  JOB_STATUS.assigned,
  JOB_STATUS.in_progress,
  JOB_STATUS.completed,
  JOB_STATUS.cancelled,
]

function isStatusFilter(value: string): value is StatusFilter {
  return value === 'all' || isJobStatus(value)
}

function clearPaymentSearchParams(params: URLSearchParams) {
  params.delete('panel')
  params.delete('payment')
  params.delete('detail_status')
  params.delete('create_client_id')
  params.delete('create_job_id')
  params.delete('create_invoice_id')
  params.delete('create_amount')
  params.delete('report_job')
  params.delete('collect')
}

const CALENDAR_SURFACE_CLASS = '-m-6 min-h-[calc(100vh-72px)] bg-white'
const CALENDAR_PANEL_CLASS =
  'overflow-hidden rounded-[20px] border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'

// Temporarily pause calendar interaction features while keeping the visual UI.
// Flip these back to true to restore:
// - reorder technician columns
// - drag/drop assign, reassign, and unassign
// - drag resize intervention duration
const ENABLE_AGENDA_TECHNICIAN_COLUMN_REORDER = false
const ENABLE_AGENDA_DRAG_DROP_ASSIGNMENT = false
const ENABLE_AGENDA_RESIZE_DURATION = false
const AGENDA_TECHNICIAN_PAGE_SIZE = 10

type UnassignedJobsCache = InfiniteData<ApiList<Job>>
type AgendaScheduleCache =
  | ApiResponse<AgendaSchedule>
  | InfiniteData<ApiResponse<AgendaSchedule>>

type QuerySnapshot<T> = Array<[QueryKey, T | undefined]>

function isAgendaScheduleQuery(queryKey: QueryKey) {
  return (
    queryKey[0] === 'agenda' &&
    (queryKey[1] === 'schedule' || queryKey[1] === 'technician-pages')
  )
}

function isInfiniteAgendaScheduleCache(
  cache: AgendaScheduleCache | undefined
): cache is InfiniteData<ApiResponse<AgendaSchedule>> {
  return Boolean(cache && 'pages' in cache && Array.isArray(cache.pages))
}

function restoreQuerySnapshots<T>(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshots: QuerySnapshot<T> | undefined
) {
  if (!snapshots) return
  for (const [queryKey, data] of snapshots) {
    queryClient.setQueryData(queryKey, data)
  }
}

function jobFromSchedule(schedule: AgendaSchedule | undefined, jobId: string) {
  if (!schedule) return null

  for (const row of schedule.technicians) {
    const job = row.jobs.find(entry => entry.id === jobId)
    if (job) return job
  }

  return null
}

function asUnassignedJob(job: Job): Job {
  return {
    ...job,
    status: JOB_STATUS.planned,
    assigned_technicians: [],
  }
}

function insertIntoUnassignedCache(
  cache: UnassignedJobsCache | undefined,
  job: Job,
  insertBeforeJobId?: string
): UnassignedJobsCache | undefined {
  if (!cache?.pages.length) return cache

  let removedExisting = false
  const flattened = cache.pages.flatMap(page => page.data)
  const withoutJob = flattened.filter(entry => {
    const keep = entry.id !== job.id
    if (!keep) removedExisting = true
    return keep
  })
  const insertIndex = insertBeforeJobId
    ? withoutJob.findIndex(entry => entry.id === insertBeforeJobId)
    : -1
  const nextJobs = [...withoutJob]
  nextJobs.splice(insertIndex >= 0 ? insertIndex : nextJobs.length, 0, job)

  let cursor = 0
  const pages = cache.pages.map((page, pageIndex) => {
    const isLastPage = pageIndex === cache.pages.length - 1
    const originalSize = page.data.length
    const pageSize = isLastPage
      ? nextJobs.length - cursor
      : Math.min(originalSize, nextJobs.length - cursor)
    const data = nextJobs.slice(cursor, cursor + pageSize)
    cursor += pageSize

    return {
      ...page,
      data,
      metadata: {
        ...page.metadata,
        total: page.metadata.total + (removedExisting ? 0 : 1),
        to:
          page.metadata.from === null
            ? data.length > 0
              ? data.length
              : null
            : page.metadata.from + data.length - 1,
      },
    }
  })

  return { ...cache, pages }
}

function jobFromUnassignedCache(
  cache: UnassignedJobsCache | undefined,
  jobId: string
) {
  if (!cache) return null

  for (const page of cache.pages) {
    const job = page.data.find(entry => entry.id === jobId)
    if (job) return job
  }

  return null
}

function statusCountsFromSchedule(
  schedule: AgendaSchedule | undefined
): Record<StatusFilter, number> {
  const counts: Record<StatusFilter, number> = {
    all: 0,
    [JOB_STATUS.to_plan]: 0,
    [JOB_STATUS.planned]: 0,
    [JOB_STATUS.assigned]: 0,
    [JOB_STATUS.in_progress]: 0,
    [JOB_STATUS.completed]: 0,
    [JOB_STATUS.cancelled]: 0,
  }
  const seen = new Set<string>()

  for (const row of schedule?.technicians ?? []) {
    for (const job of row.jobs) {
      if (seen.has(job.id)) continue
      seen.add(job.id)
      counts.all += 1
      counts[job.status] += 1
    }
  }

  return counts
}

function statusCountsFromSummary(
  summary: Record<string, number> | undefined
): Record<StatusFilter, number> | null {
  if (!summary) return null

  const counts: Record<StatusFilter, number> = {
    all: summary.all ?? 0,
    [JOB_STATUS.to_plan]: summary[JOB_STATUS.to_plan] ?? 0,
    [JOB_STATUS.planned]: summary[JOB_STATUS.planned] ?? 0,
    [JOB_STATUS.assigned]: summary[JOB_STATUS.assigned] ?? 0,
    [JOB_STATUS.in_progress]: summary[JOB_STATUS.in_progress] ?? 0,
    [JOB_STATUS.completed]: summary[JOB_STATUS.completed] ?? 0,
    [JOB_STATUS.cancelled]: summary[JOB_STATUS.cancelled] ?? 0,
  }

  return counts
}

function mergeAgendaSchedulePages(
  pages: ApiResponse<AgendaSchedule>[] | undefined
): AgendaSchedule | undefined {
  const first = pages?.find(page => page.data)?.data
  if (!first) return undefined

  const seen = new Set<string>()
  const technicians: AgendaSchedule['technicians'] = []

  for (const page of pages ?? []) {
    for (const row of page.data?.technicians ?? []) {
      if (seen.has(row.technician.id)) continue
      seen.add(row.technician.id)
      technicians.push(row)
    }
  }

  return {
    ...first,
    technicians,
  }
}

function jobFromUnassignedCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  jobId: string
) {
  const caches = queryClient.getQueriesData<UnassignedJobsCache>({
    queryKey: ['agenda', 'unassigned'],
  })

  for (const [, cache] of caches) {
    const job = jobFromUnassignedCache(cache, jobId)
    if (job) return job
  }

  return null
}

function removeFromUnassignedCache(
  cache: UnassignedJobsCache | undefined,
  jobId: string
): UnassignedJobsCache | undefined {
  if (!cache?.pages.length) return cache

  const removed = cache.pages.some(page =>
    page.data.some(job => job.id === jobId)
  )
  if (!removed) return cache

  const pages = cache.pages.map(page => {
    const data = page.data.filter(job => job.id !== jobId)

    return {
      ...page,
      data,
      metadata: {
        ...page.metadata,
        total: Math.max(0, page.metadata.total - (removed ? 1 : 0)),
        to:
          page.metadata.from === null
            ? data.length > 0
              ? data.length
              : null
            : page.metadata.from + data.length - 1,
      },
    }
  })

  return { ...cache, pages }
}

function removeFromAgendaScheduleResponse(
  response: ApiResponse<AgendaSchedule> | undefined,
  jobId: string
): ApiResponse<AgendaSchedule> | undefined {
  if (!response?.data) return response

  return {
    ...response,
    data: {
      ...response.data,
      technicians: response.data.technicians.map(row => ({
        ...row,
        jobs: row.jobs.filter(job => job.id !== jobId),
      })),
    },
  }
}

function removeFromAgendaScheduleCache(
  cache: AgendaScheduleCache | undefined,
  jobId: string
): AgendaScheduleCache | undefined {
  if (isInfiniteAgendaScheduleCache(cache)) {
    return {
      ...cache,
      pages: cache.pages.map(
        page => removeFromAgendaScheduleResponse(page, jobId) ?? page
      ),
    }
  }

  return removeFromAgendaScheduleResponse(cache, jobId)
}

function patchAgendaScheduleResponse(
  response: ApiResponse<AgendaSchedule> | undefined,
  jobId: string,
  payload: ScheduleJobPayload | ResizeJobPayload,
  sourceJob?: Job | null,
  knownTechnicians: Technician[] = []
): ApiResponse<AgendaSchedule> | undefined {
  return response?.data
    ? {
        ...response,
        data: applyAgendaJobSchedulePatch(
          response.data,
          jobId,
          payload,
          sourceJob,
          knownTechnicians
        ),
      }
    : response
}

function patchAgendaScheduleCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  jobId: string,
  payload: ScheduleJobPayload | ResizeJobPayload,
  sourceJob?: Job | null,
  knownTechnicians: Technician[] = []
) {
  queryClient.setQueriesData<AgendaScheduleCache>(
    {
      predicate: query => isAgendaScheduleQuery(query.queryKey),
    },
    cache => {
      if (isInfiniteAgendaScheduleCache(cache)) {
        return {
          ...cache,
          pages: cache.pages.map(
            page =>
              patchAgendaScheduleResponse(
                page,
                jobId,
                payload,
                sourceJob,
                knownTechnicians
              ) ?? page
          ),
        }
      }

      return patchAgendaScheduleResponse(
        cache,
        jobId,
        payload,
        sourceJob,
        knownTechnicians
      )
    }
  )
}

function shiftDate(date: Date, view: View, dir: number): Date {
  const d = new Date(date)
  if (view === 'day') d.setDate(d.getDate() + dir)
  else if (view === 'week') d.setDate(d.getDate() + dir * 7)
  else d.setMonth(d.getMonth() + dir)
  return d
}

// Human date label for the stepper. Week/month ranges stay compact; the
// single-day header uses the full weekday and month names.
function formatStepperDate(
  iso: string,
  locale: string,
  withWeekday: boolean
): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return new Intl.DateTimeFormat(locale, {
    weekday: withWeekday ? 'short' : undefined,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d)
}

function formatToolbarDate(date: Date, view: View, locale: string): string {
  if (view === 'month') {
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
    }).format(date)
  }

  if (view === 'week') {
    const { from, to } = rangeFor(date, 'week')
    const fromDate = new Date(from)
    const toDate = new Date(to)
    const sameMonth = fromDate.getMonth() === toDate.getMonth()
    const sameYear = fromDate.getFullYear() === toDate.getFullYear()

    if (sameMonth && sameYear) {
      const monthYear = new Intl.DateTimeFormat(locale, {
        month: 'short',
        year: 'numeric',
      }).format(toDate)
      return `${fromDate.getDate()}-${toDate.getDate()} ${monthYear}`
    }

    return `${formatStepperDate(from, locale, false)} - ${formatStepperDate(
      to,
      locale,
      false
    )}`
  }

  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function periodShortcutLabel(view: View): string {
  if (view === 'week') return 'agenda.period.thisWeek'
  if (view === 'month') return 'agenda.period.thisMonth'
  return 'agenda.today'
}

// 2.3.1 Agenda — scheduling view (reproduces agenda.md). Day/Week/Month toggle,
// status + technician filters, date stepper, technician-colour grid + tray.
export default function AgendaPage() {
  const { t, i18n } = useTranslation(['jobs', 'payments'])
  const [searchParams, setSearchParams] = useSearchParams()
  useServiceCategoryOptions()
  const [view, setView] = useState<View>('day')
  const [anchor, setAnchor] = useState(new Date())
  const initialStatus = searchParams.get('status')
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>(() =>
    isJobStatus(initialStatus) ? initialStatus : 'all'
  )
  const [technicianIds, setTechnicianIds] = useState<string[] | null>(null)
  const [mutatingJobId, setMutatingJobId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const range = useMemo(() => rangeFor(anchor, view), [anchor, view])
  const apiView: AgendaQuery['view'] = view === 'month' ? 'week' : view

  // Active technicians for the multi-select filter (AC-2.3.1 step 5). The API
  // filters on the DISPLAYED status, so an active technician currently on an
  // in-progress job sits in the "on_intervention" bucket and must be asked for
  // explicitly to stay in this list.
  const {
    data: techData,
    isLoading: isTechniciansLoading,
    isError: isTechniciansError,
  } = useTechniciansList({
    status: ['active', 'on_intervention'],
    limit: 100,
  })
  const technicians = useMemo(
    () => (techData?.data ?? []).filter(tech => tech.status === 'active'),
    [techData]
  )

  // Feedback #15 — selected job opens the right-side detail drawer over the
  // calendar instead of navigating to /jobs/:id. We mirror it into a `job`
  // search param so the drawer survives refresh / is deep-linkable, while the
  // calendar (view/anchor/filters) stays mounted and unchanged underneath.
  const selectedJobId = searchParams.get('job')
  const reportParam = searchParams.get('report')
  const createParam = searchParams.get('create')
  const editJobId = searchParams.get('edit_job')
  const rescheduleOpen = searchParams.get('reschedule') === '1'
  const paymentParam = searchParams.get('payment')
  const activePaymentPanel = searchParams.get('panel')
  const createJobOpen = createParam === '1'
  const reportMode =
    reportParam === 'create' || reportParam === 'edit' ? reportParam : null
  const paymentCreateOpen = paymentParam === 'create'
  const paymentDetailOpen = activePaymentPanel === 'detail' && !!paymentParam
  const autoOpenPaymentStatus = searchParams.get('detail_status') === '1'
  const createClientId = searchParams.get('create_client_id') ?? ''
  const createJobId = searchParams.get('create_job_id') ?? ''
  const createInvoiceId = searchParams.get('create_invoice_id') ?? ''
  const createAmount = searchParams.get('create_amount') ?? ''
  const reportJob = searchParams.get('report_job') ?? ''

  const openCreateJobForm = () => {
    const next = new URLSearchParams(searchParams)
    next.set('create', '1')
    next.delete('job')
    next.delete('edit_job')
    next.delete('reschedule')
    next.delete('tab')
    next.delete('report')
    setSearchParams(next, { replace: false })
  }

  const closeCreateJobForm = (destination?: string) => {
    const next = new URLSearchParams(searchParams)
    next.delete('create')
    if (destination?.startsWith('/calendar?')) {
      const destinationParams = new URLSearchParams(destination.split('?')[1])
      const jobId = destinationParams.get('job')
      if (jobId) next.set('job', jobId)
    }
    setSearchParams(next, { replace: false })
  }

  const closeEditJobForm = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('edit_job')
    if (editJobId) next.set('job', editJobId)
    setSearchParams(next, { replace: false })
  }

  const openJob = (job: Job) => {
    const next = new URLSearchParams(searchParams)
    next.set('job', job.id)
    next.delete('edit_job')
    next.delete('reschedule')
    next.delete('tab')
    next.delete('report')
    clearPaymentSearchParams(next)
    setSearchParams(next, { replace: false })
  }

  const closeDrawer = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('job')
    next.delete('tab')
    next.delete('report')
    next.delete('reschedule')
    clearPaymentSearchParams(next)
    setSearchParams(next, { replace: false })
  }

  const closePaymentDrawer = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        clearPaymentSearchParams(next)
        return next
      },
      { replace: false }
    )
  }, [setSearchParams])

  const openReportDrawer = useCallback(
    (jobId: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          clearPaymentSearchParams(next)
          next.delete('reschedule')
          next.set('job', jobId)
          next.set('tab', 'reports')
          next.set('report', 'create')
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  const closeReportDrawer = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('report')
    next.set('tab', 'reports')
    setSearchParams(next, { replace: false })
  }

  const closeRescheduleDrawer = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('reschedule')
    setSearchParams(next, { replace: false })
  }

  const openMonthDay = (day: Date) => {
    setAnchor(new Date(day))
    setView('day')
  }

  const handleStatusFilterChange = (value: string) => {
    if (!isStatusFilter(value)) return
    setStatusFilter(value)
    const next = new URLSearchParams(searchParams)
    if (value === 'all') next.delete('status')
    else next.set('status', value)
    setSearchParams(next, { replace: false })
  }

  const {
    data: agendaPagesData,
    isLoading: isAgendaLoading,
    isError: isAgendaError,
    fetchNextPage: fetchNextTechnicianPage,
    hasNextPage: hasNextTechnicianPage,
    isFetchingNextPage: isFetchingNextTechnicianPage,
  } = useAgendaTechnicianPages({
    date_from: range.from,
    date_to: range.to,
    view: apiView,
    status: statusFilter === 'all' ? undefined : statusFilter,
    technician_id: technicianIds?.length ? technicianIds : undefined,
    technician_limit: AGENDA_TECHNICIAN_PAGE_SIZE,
  })

  const schedule = useMemo(() => {
    const mergedSchedule = mergeAgendaSchedulePages(agendaPagesData?.pages)
    if (!mergedSchedule || technicianIds?.length !== 0) return mergedSchedule

    return { ...mergedSchedule, technicians: [] }
  }, [agendaPagesData, technicianIds])
  const loadedTechnicians = useMemo(
    () => schedule?.technicians.map(row => row.technician) ?? [],
    [schedule]
  )
  const isCalendarLoading = isAgendaLoading || isTechniciansLoading
  const isCalendarError = isAgendaError || isTechniciansError
  useAgendaJobChanges(!isCalendarLoading && !isCalendarError)
  // Preload job details behind the calendar so the detail drawer opens from
  // cache (docs/AGENDA_JOB_PANEL_LOADING.md).
  const { queue: jobPreloadQueue, rootRef: jobPreloadRootRef } =
    useAgendaJobPreload({
      view,
      enabled: !isCalendarLoading && !isCalendarError,
      resetKey: [
        view,
        range.from,
        range.to,
        statusFilter,
        technicianIds === null ? 'all' : technicianIds.join(','),
      ].join('|'),
      proximityPaused:
        selectedJobId !== null || createJobOpen || editJobId !== null,
    })
  const statusCounts = useMemo(
    () =>
      statusCountsFromSummary(agendaPagesData?.pages[0]?.metadata?.summary) ??
      statusCountsFromSchedule(schedule),
    [agendaPagesData, schedule]
  )
  const loadMoreTechnicians = useCallback(() => {
    if (!hasNextTechnicianPage || isFetchingNextTechnicianPage) return
    void fetchNextTechnicianPage()
  }, [
    fetchNextTechnicianPage,
    hasNextTechnicianPage,
    isFetchingNextTechnicianPage,
  ])

  const scheduleJobMutation = useMutation({
    mutationFn: ({
      jobId,
      payload,
    }: {
      jobId: string
      payload: ScheduleJobPayload
      sourceJob?: Job | null
    }) => jobsApi.updateAgendaSchedule(jobId, payload),
    onMutate: async ({ jobId, payload, sourceJob }) => {
      await Promise.all([
        queryClient.cancelQueries({
          predicate: query => isAgendaScheduleQuery(query.queryKey),
        }),
        queryClient.cancelQueries({ queryKey: ['agenda', 'unassigned'] }),
      ])

      const agendaSnapshots = queryClient.getQueriesData<AgendaScheduleCache>({
        predicate: query => isAgendaScheduleQuery(query.queryKey),
      })
      const unassignedSnapshots =
        queryClient.getQueriesData<UnassignedJobsCache>({
          queryKey: ['agenda', 'unassigned'],
        })

      patchAgendaScheduleCaches(
        queryClient,
        jobId,
        payload,
        sourceJob,
        loadedTechnicians
      )
      if (payload.technician_ids.length > 0) {
        queryClient.setQueriesData<UnassignedJobsCache>(
          { queryKey: ['agenda', 'unassigned'] },
          cache => removeFromUnassignedCache(cache, jobId)
        )
      }

      return { agendaSnapshots, unassignedSnapshots }
    },
    onError: (_, __, context) => {
      restoreQuerySnapshots(queryClient, context?.agendaSnapshots)
      restoreQuerySnapshots(queryClient, context?.unassignedSnapshots)
      void queryClient.invalidateQueries({
        queryKey: ['agenda'],
        refetchType: 'inactive',
      })
    },
    onSuccess: (response, { jobId, payload }) => {
      patchAgendaScheduleCaches(
        queryClient,
        jobId,
        payload,
        response.data,
        loadedTechnicians
      )
      void queryClient.invalidateQueries({
        queryKey: ['agenda'],
        refetchType: 'inactive',
      })
      void queryClient.invalidateQueries({
        queryKey: ['jobs', 'detail', jobId],
      })
      invalidatePortalMetricQueries(queryClient)
    },
  })
  const resizeJobMutation = useMutation({
    mutationFn: ({
      jobId,
      payload,
    }: {
      jobId: string
      payload: ResizeJobPayload
    }) => jobsApi.updateAgendaTime(jobId, payload),
    onMutate: async ({ jobId, payload }) => {
      await queryClient.cancelQueries({
        predicate: query => isAgendaScheduleQuery(query.queryKey),
      })
      const agendaSnapshots = queryClient.getQueriesData<AgendaScheduleCache>({
        predicate: query => isAgendaScheduleQuery(query.queryKey),
      })

      patchAgendaScheduleCaches(
        queryClient,
        jobId,
        payload,
        undefined,
        loadedTechnicians
      )

      return { agendaSnapshots }
    },
    onError: (_, __, context) => {
      restoreQuerySnapshots(queryClient, context?.agendaSnapshots)
      void queryClient.invalidateQueries({
        predicate: query => isAgendaScheduleQuery(query.queryKey),
        refetchType: 'inactive',
      })
    },
    onSuccess: (response, { jobId, payload }) => {
      patchAgendaScheduleCaches(
        queryClient,
        jobId,
        payload,
        response.data,
        loadedTechnicians
      )
      void queryClient.invalidateQueries({
        predicate: query => isAgendaScheduleQuery(query.queryKey),
        refetchType: 'inactive',
      })
      void queryClient.invalidateQueries({
        queryKey: ['jobs', 'detail', jobId],
      })
      invalidatePortalMetricQueries(queryClient)
    },
  })
  const unassignJobMutation = useMutation({
    mutationFn: ({ jobId }: { jobId: string }) =>
      jobsApi.updateAgendaSchedule(jobId, { technician_ids: [] }),
    onSuccess: (_, { jobId }) => {
      void queryClient.invalidateQueries({
        predicate: query => isAgendaScheduleQuery(query.queryKey),
      })
      void queryClient.invalidateQueries({
        queryKey: ['jobs', 'detail', jobId],
      })
      invalidatePortalMetricQueries(queryClient)
    },
  })

  const handleScheduleJob = useCallback<
    AgendaMutationHandlers['onScheduleJob']
  >(
    (jobId, payload) => {
      if (mutatingJobId) return
      setMutatingJobId(jobId)
      const sourceJob =
        jobFromSchedule(schedule, jobId) ??
        jobFromUnassignedCaches(queryClient, jobId)
      scheduleJobMutation.mutate(
        { jobId, payload, sourceJob },
        {
          onSettled: () => setMutatingJobId(null),
        }
      )
    },
    [mutatingJobId, queryClient, schedule, scheduleJobMutation]
  )
  const handleUnassignJob = useCallback<
    AgendaMutationHandlers['onUnassignJob']
  >(
    (jobId, options?: UnassignJobOptions) => {
      if (mutatingJobId) return
      setMutatingJobId(jobId)
      const job = jobFromSchedule(schedule, jobId)
      if (job) {
        queryClient.setQueriesData<UnassignedJobsCache>(
          { queryKey: ['agenda', 'unassigned'] },
          cache =>
            insertIntoUnassignedCache(
              cache,
              asUnassignedJob(job),
              options?.insertBeforeJobId
            )
        )
        queryClient.setQueriesData<AgendaScheduleCache>(
          {
            predicate: query => isAgendaScheduleQuery(query.queryKey),
          },
          cache => removeFromAgendaScheduleCache(cache, jobId)
        )
      }
      unassignJobMutation.mutate(
        { jobId },
        {
          onError: () => {
            void queryClient.invalidateQueries({ queryKey: ['agenda'] })
          },
          onSettled: () => setMutatingJobId(null),
        }
      )
    },
    [mutatingJobId, queryClient, schedule, unassignJobMutation]
  )
  const handleResizeJob = useCallback<AgendaMutationHandlers['onResizeJob']>(
    (jobId, payload: ResizeJobPayload) => {
      if (mutatingJobId) return
      setMutatingJobId(jobId)
      resizeJobMutation.mutate(
        { jobId, payload },
        {
          onSettled: () => setMutatingJobId(null),
        }
      )
    },
    [mutatingJobId, resizeJobMutation]
  )
  const agendaMutationHandlers = useMemo<AgendaMutationHandlers>(
    () => ({
      onScheduleJob: handleScheduleJob,
      onUnassignJob: handleUnassignJob,
      onResizeJob: handleResizeJob,
      isMutatingJobId: mutatingJobId,
    }),
    [handleScheduleJob, handleUnassignJob, handleResizeJob, mutatingJobId]
  )
  const shouldFillCalendarViewport = view === 'month' || view === 'week'

  return (
    <JobDetailPreloadRoot
      queue={jobPreloadQueue}
      rootRef={jobPreloadRootRef}
      className={`${CALENDAR_SURFACE_CLASS} ${
        shouldFillCalendarViewport
          ? 'xl:flex xl:h-[calc(100dvh-72px)] xl:min-h-0 xl:flex-col'
          : ''
      }`}
    >
      <div
        className={`flex min-h-[74px] flex-wrap items-center gap-3 overflow-visible border-b border-[#e7e9ef] bg-white px-6 py-4 shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)] ${
          shouldFillCalendarViewport ? 'xl:shrink-0' : ''
        }`}
      >
        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-2'>
          <div className='flex shrink-0 items-center gap-1'>
            <Button
              variant='ghost'
              size='icon'
              className='h-10 w-10 rounded-xl bg-[#f4f5f8] text-[#272c36] hover:bg-[#e7e9ef] hover:text-[#171a20]'
              onClick={() => setAnchor(shiftDate(anchor, view, -1))}
              aria-label={t('agenda.previous')}
            >
              <ChevronLeft className='h-5 w-5' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='h-10 w-10 rounded-xl bg-[#f4f5f8] text-[#272c36] hover:bg-[#e7e9ef] hover:text-[#171a20]'
              onClick={() => setAnchor(shiftDate(anchor, view, 1))}
              aria-label={t('agenda.next')}
            >
              <ChevronRight className='h-5 w-5' />
            </Button>
          </div>
          <Button
            type='button'
            variant='outline'
            className='h-10 shrink-0 rounded-xl border-[#e7e9ef] bg-white px-4 font-sans text-base font-medium leading-none text-[#171a20] shadow-none hover:bg-[#fbfbfc]'
            onClick={() => setAnchor(new Date())}
          >
            {t(periodShortcutLabel(view))}
          </Button>
          <span className='min-w-fit shrink-0 whitespace-nowrap font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
            {formatToolbarDate(anchor, view, i18n.language)}
          </span>
          <TechnicianFilter
            technicians={technicians}
            selected={technicianIds}
            onChange={setTechnicianIds}
            schedule={schedule}
          />
          <StatusFilterDropdown
            value={statusFilter}
            counts={statusCounts}
            onChange={handleStatusFilterChange}
          />
        </div>

        <div className='ml-auto flex min-w-fit flex-wrap items-center gap-2'>
          <div className='inline-flex h-[42px] rounded-xl bg-[#f4f5f8] p-1'>
            {(['day', 'week', 'month'] as View[]).map(v => (
              <button
                type='button'
                key={v}
                onClick={() => setView(v)}
                className={`h-[34px] rounded-lg px-4 font-sans text-sm font-medium leading-none transition-colors ${
                  view === v
                    ? 'border border-[#ff5a1f] bg-[#fff1ea] text-[#ff5a1f]'
                    : 'border border-transparent text-[#3d434e] hover:bg-white'
                }`}
              >
                {t(`agenda.views.${v}`)}
              </button>
            ))}
          </div>
          <Permit module='agenda' action='edit'>
            <Button
              className='h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 font-sans text-sm font-medium leading-none text-white shadow-none hover:bg-[#ef4f16]'
              onClick={openCreateJobForm}
            >
              <Plus className='mr-2 h-4 w-4' /> {t('agenda.toPlanButton')}
            </Button>
          </Permit>
        </div>
      </div>

      <div
        className={`space-y-2 bg-[#f4f5f8] px-6 py-4 ${
          shouldFillCalendarViewport
            ? 'xl:flex xl:min-h-0 xl:flex-1 xl:flex-col'
            : ''
        }`}
      >
        <div className={shouldFillCalendarViewport ? 'xl:shrink-0' : undefined}>
          <UnassignedTray
            onJobClick={openJob}
            onUnassignJob={handleUnassignJob}
            isMutatingJobId={mutatingJobId}
            enableDragDropAssignment={ENABLE_AGENDA_DRAG_DROP_ASSIGNMENT}
          />
        </div>

        <div
          className={`${CALENDAR_PANEL_CLASS} ${
            shouldFillCalendarViewport
              ? 'xl:flex xl:min-h-0 xl:flex-1 xl:flex-col'
              : ''
          }`}
        >
          {isCalendarLoading && <Skeleton className='h-64 w-full' />}

          {isCalendarError && !isCalendarLoading && (
            <div className='py-16 text-center text-destructive'>
              {t('agenda.loadError')}
            </div>
          )}

          {!isCalendarLoading && !isCalendarError && schedule && (
            <AgendaGrid
              schedule={schedule}
              technicians={loadedTechnicians}
              view={view}
              anchor={anchor}
              onEntryClick={openJob}
              onMonthDayClick={openMonthDay}
              onLoadMoreTechnicians={loadMoreTechnicians}
              hasMoreTechnicians={Boolean(hasNextTechnicianPage)}
              isLoadingMoreTechnicians={isFetchingNextTechnicianPage}
              enableTechnicianColumnReorder={
                ENABLE_AGENDA_TECHNICIAN_COLUMN_REORDER
              }
              enableDragDropAssignment={ENABLE_AGENDA_DRAG_DROP_ASSIGNMENT}
              enableResizeDuration={ENABLE_AGENDA_RESIZE_DURATION}
              {...agendaMutationHandlers}
            />
          )}
        </div>
      </div>

      {/* Feedback #15 — right-side job-details drawer overlaying the calendar. */}
      <JobDetailDrawer
        jobId={
          reportMode || paymentCreateOpen || rescheduleOpen
            ? null
            : selectedJobId
        }
        onClose={closeDrawer}
      />

      <PaymentDrawerShell
        open={paymentCreateOpen}
        title={t('payments:form.drawerTitle')}
        onClose={closePaymentDrawer}
        hideHeader
      >
        <PaymentCreateDrawerContent
          key={`create:${createClientId}:${createJobId}:${createInvoiceId}:${createAmount}:${reportJob}`}
          initialClientId={createClientId}
          initialJobId={createJobId}
          initialInvoiceId={createInvoiceId}
          initialAmount={createAmount}
          reportJob={reportJob}
          presentation='intervention'
          onCancel={closePaymentDrawer}
          onRecorded={closePaymentDrawer}
          onReportRequested={openReportDrawer}
        />
      </PaymentDrawerShell>

      <PaymentDrawerShell
        open={paymentDetailOpen}
        title={t('payments:detail.drawerTitle')}
        onClose={closePaymentDrawer}
        hideHeader
      >
        {paymentDetailOpen && paymentParam ? (
          <PaymentDetailDrawerContent
            key={`detail:${paymentParam}:${autoOpenPaymentStatus ? 'status' : 'view'}`}
            paymentId={paymentParam}
            autoOpenStatus={autoOpenPaymentStatus}
            presentation='intervention'
            backActionLabel={t('payments:form.backToIntervention')}
            onBack={closePaymentDrawer}
            onClose={closePaymentDrawer}
          />
        ) : null}
      </PaymentDrawerShell>

      <Dialog
        open={selectedJobId !== null && rescheduleOpen}
        onOpenChange={open => {
          if (!open) closeRescheduleDrawer()
        }}
      >
        <DialogContent
          side='right'
          className='tf-right-drawer-short gap-0 overflow-y-auto [&>button]:hidden'
          overlayClassName='bg-black/40'
        >
          <DialogTitle className='sr-only'>
            {t('dialogs.reschedule.title')}
          </DialogTitle>
          {selectedJobId && (
            <RescheduleJobDrawerContent
              key={selectedJobId}
              jobId={selectedJobId}
              onClose={closeRescheduleDrawer}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={selectedJobId !== null && reportMode !== null}
        onOpenChange={open => {
          if (!open) closeReportDrawer()
        }}
      >
        <DialogContent
          side='right'
          className='tf-right-drawer-short gap-0 [&>button]:hidden'
          overlayClassName='bg-black/40'
        >
          <DialogTitle className='sr-only'>
            {reportMode === 'create'
              ? t('report.createTitle')
              : t('report.editTitle')}
          </DialogTitle>
          {selectedJobId && reportMode && (
            <JobReportWizardPage
              key={`${selectedJobId}:${reportMode}`}
              mode={reportMode}
              jobId={selectedJobId}
              presentation='drawer'
              onClose={closeReportDrawer}
            />
          )}
        </DialogContent>
      </Dialog>

      {createJobOpen && (
        <JobFormDialog mode='create' onClose={closeCreateJobForm} />
      )}
      {editJobId && (
        <JobFormDialog
          mode='edit'
          jobId={editJobId}
          onClose={closeEditJobForm}
        />
      )}
    </JobDetailPreloadRoot>
  )
}

function StatusFilterDropdown({
  value,
  counts,
  onChange,
}: {
  value: StatusFilter
  counts: Record<StatusFilter, number>
  onChange: (value: string) => void
}) {
  const { t } = useTranslation('jobs')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className='relative'>
      <Button
        type='button'
        variant='filter'
        onClick={() => setOpen(current => !current)}
        aria-expanded={open}
        className={cn(
          'h-10 w-[149px] justify-between rounded-xl border-[#e7e9ef] bg-white px-3 font-sans text-sm font-normal leading-[1.4] text-[#171a20] shadow-none',
          FORM_FILTER_HOVER_CLASS,
          open && FORM_CONTROL_OPEN_CLASS
        )}
      >
        <span className='flex min-w-0 items-center gap-2'>
          <CheckCircle className='h-4 w-4 shrink-0 text-[#272c36]' />
          <span className='truncate'>{t(`agenda.statusFilter.${value}`)}</span>
        </span>
        <ChevronDown className='ml-2 h-4 w-4 shrink-0 text-[#272c36] opacity-70' />
      </Button>

      {open && (
        <div className='absolute left-0 z-50 mt-1 w-[240px] overflow-hidden rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)]'>
          <div>
            {statusOptions.map(option => {
              const selected = option === value
              return (
                <button
                  type='button'
                  key={option}
                  onClick={() => {
                    onChange(option)
                    setOpen(false)
                  }}
                  className={cn(
                    'flex min-h-12 w-full items-center gap-4 rounded-xl px-4 py-2 text-left font-sans text-sm font-normal leading-[1.4] text-[#171a20] hover:bg-[#f4f5f8]',
                    selected && 'bg-white'
                  )}
                >
                  <span className='flex w-6 shrink-0 items-center justify-center text-[#10b981]'>
                    {selected ? (
                      <Check className='h-5 w-5 stroke-[2.2]' />
                    ) : null}
                  </span>
                  <span className='min-w-0 flex-1 truncate'>
                    {t(`agenda.statusFilter.${option}`)}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 tabular-nums text-sm font-normal leading-[1.4] text-[#171a20]',
                      option === JOB_STATUS.cancelled &&
                        counts[option] === 0 &&
                        'text-[#d92d20]'
                    )}
                  >
                    {counts[option]}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const TECHNICIAN_FILTER_AVATAR_GRADIENTS = [
  'linear-gradient(149.66deg, #ebf1fe 0%, #2e5ce6 100%)',
  'linear-gradient(149.66deg, #e2f5f3 0%, #0e8a7a 100%)',
  'linear-gradient(149.66deg, #f2ebfd 0%, #7b3bea 100%)',
  'linear-gradient(149.66deg, #fcf2dc 0%, #c87b08 100%)',
  'linear-gradient(149.66deg, #fdeaea 0%, #d02f2f 100%)',
  'linear-gradient(149.66deg, #fde9f3 0%, #db2777 100%)',
  'linear-gradient(149.66deg, #e0f5fa 0%, #0891b2 100%)',
  'linear-gradient(149.66deg, #ffefe7 0%, #ea4a10 100%)',
  'linear-gradient(149.66deg, #f1f7e3 0%, #65a30d 100%)',
]

function minutesBetween(start: string | null, end: string | null): number {
  if (!start || !end) return 0
  const [startHours, startMinutes] = start.split(':').map(Number)
  const [endHours, endMinutes] = end.split(':').map(Number)
  if (
    !Number.isFinite(startHours) ||
    !Number.isFinite(startMinutes) ||
    !Number.isFinite(endHours) ||
    !Number.isFinite(endMinutes)
  ) {
    return 0
  }
  const diff = endHours * 60 + endMinutes - (startHours * 60 + startMinutes)
  return Math.max(diff, 0)
}

function formatTechnicianWorkload(
  schedule: AgendaSchedule | undefined,
  technicianId: string,
  locale: string
): string {
  const row = schedule?.technicians.find(
    entry => entry.technician.id === technicianId
  )
  const minutes =
    row?.jobs.reduce(
      (total, job) => total + minutesBetween(job.start_time, job.end_time),
      0
    ) ?? 0
  const hours = minutes / 60
  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: hours % 1 === 0 ? 0 : 1,
  }).format(hours)
  return `${formatted}h`
}

// Multi-select technician filter matching the Figma menu composition. An empty
// selection means "all technicians" for the API, but renders as all checked.
function TechnicianFilter({
  technicians,
  selected,
  onChange,
  schedule,
}: {
  technicians: Technician[]
  selected: string[] | null
  onChange: (ids: string[] | null) => void
  schedule: AgendaSchedule | undefined
}) {
  const { t, i18n } = useTranslation('jobs')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const technicianIds = useMemo(
    () => technicians.map(technician => technician.id),
    [technicians]
  )
  const effectiveSelected = selected ?? technicianIds
  const allSelected =
    technicianIds.length > 0 &&
    technicianIds.every(technicianId =>
      effectiveSelected.includes(technicianId)
    )

  const toggle = (id: string) => {
    const base = selected ?? technicianIds
    const next = base.includes(id)
      ? base.filter(current => current !== id)
      : [...base, id]
    onChange(next.length === technicianIds.length ? null : next)
  }

  const selectAll = () => onChange(null)
  const toggleAll = () => onChange(allSelected ? [] : null)

  const label =
    selected === null
      ? t('agenda.technicianFilter.placeholder')
      : t('agenda.technicianFilter.selected', { count: selected.length })

  return (
    <div ref={ref} className='relative'>
      <Button
        type='button'
        variant='filter'
        size='sm'
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className={cn(
          'h-10 w-[148px] justify-between rounded-xl border-[#e7e9ef] bg-white px-3 font-sans text-sm font-normal leading-[1.4] text-[#171a20] shadow-none',
          FORM_FILTER_HOVER_CLASS,
          open && FORM_CONTROL_OPEN_CLASS
        )}
      >
        <span className='flex min-w-0 items-center gap-2'>
          <Users className='h-4 w-4 shrink-0 text-[#272c36]' />
          <span className='truncate'>{label}</span>
        </span>
        <ChevronDown className='ml-2 h-4 w-4 shrink-0 opacity-60' />
      </Button>

      {open && (
        <div className='absolute left-0 z-50 mt-1 h-[480px] w-[280px] overflow-hidden rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)]'>
          <div className='flex h-10 items-center gap-3 px-2 py-2'>
            <span className='min-w-0 flex-1 text-xs font-medium leading-4 text-[#3d434e]'>
              {t('agenda.technicianFilter.count', {
                count: technicians.length,
              })}
            </span>
            <button
              type='button'
              className='text-xs font-medium leading-4 text-[#3d434e]'
              onClick={selectAll}
            >
              {t('agenda.technicianFilter.selectAll')}
            </button>
            <button
              type='button'
              aria-pressed={allSelected}
              aria-label={t('agenda.technicianFilter.selectAll')}
              onClick={toggleAll}
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-lg border',
                allSelected
                  ? 'border-[#10b981] bg-[#10b981] text-[#ecfdf5]'
                  : 'border-[#e7e9ef] bg-white text-transparent'
              )}
            >
              <Check className='size-4 stroke-[1.6]' />
            </button>
          </div>

          <div className='flex h-[17px] items-center px-2 py-2'>
            <div className='h-px w-full bg-[#e7e9ef]' />
          </div>

          <div className='h-[407px] overflow-y-auto rounded-lg'>
            {technicians.length === 0 ? (
              <p className='px-2 py-3 text-center text-sm text-[#6c7285]'>
                {t('agenda.technicianFilter.empty')}
              </p>
            ) : (
              technicians.map((tech, index) => {
                const checked = effectiveSelected.includes(tech.id)
                const workload = formatTechnicianWorkload(
                  schedule,
                  tech.id,
                  i18n.language
                )
                return (
                  <button
                    key={tech.id}
                    type='button'
                    className='flex h-12 w-full items-center gap-3 px-2 py-2 text-left hover:bg-[#fbfbfc]'
                    onClick={() => toggle(tech.id)}
                  >
                    <span className='flex w-[165px] min-w-0 items-center gap-2'>
                      <Avatar
                        name={tech.full_name}
                        src={tech.avatar_url}
                        className='size-8 text-sm font-semibold leading-[1.2] text-white'
                        style={{
                          background:
                            tech.color ??
                            TECHNICIAN_FILTER_AVATAR_GRADIENTS[
                              index % TECHNICIAN_FILTER_AVATAR_GRADIENTS.length
                            ],
                        }}
                      >
                        {tech.avatar_url
                          ? undefined
                          : initialsFromName(tech.full_name)}
                      </Avatar>
                      <span className='truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
                        {technicianFirstName(tech.full_name)}
                      </span>
                    </span>
                    <span className='w-[35px] shrink-0 text-xs font-medium leading-4 text-[#3d434e]'>
                      {workload}
                    </span>
                    <span
                      className={cn(
                        'flex size-6 shrink-0 items-center justify-center rounded-lg border',
                        checked
                          ? 'border-[#10b981] bg-[#10b981] text-[#ecfdf5]'
                          : 'border-[#e7e9ef] bg-white text-transparent'
                      )}
                    >
                      <Check className='size-4 stroke-[1.6]' />
                    </span>
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
